import type { CartLine, PosSearchResult } from '../pos.schema';

// Map a loaded shop-sales order (GET /shop-sales/:orderNumber, proxied through the
// device) into cart lines. Field access is defensive — the legacy order shape
// nests product/card rows — and should be tuned against real responses.
const num = (...candidates: unknown[]): number => {
    for (const c of candidates) {
        const n = Number(c);
        if (Number.isFinite(n) && n !== 0) return n;
    }
    return 0;
};

// Paid-online orders are finalized (order completion via the admin API), never
// charged again at the register.
export function orderIsPaid(data: Record<string, unknown> | null | undefined): boolean {
    return (data as { paymentStatus?: string } | null | undefined)?.paymentStatus === 'paid';
}

// DB id of the loaded order (lookup returns the raw order row) — needed for the
// /orders/:id/status-update finalize call, which keys on id, not orderNumber.
export function orderDbId(data: Record<string, unknown> | null | undefined): string | null {
    const id = (data as { id?: number | string } | null | undefined)?.id;
    return id == null ? null : String(id);
}

export function mapOrderToLines(data: any): CartLine[] {
    const lines: CartLine[] = [];

    for (const op of data?.products ?? []) {
        const product = op.product ?? {};
        lines.push({
            id: String(product.id),
            name: product.shortName ?? product.title ?? product.name ?? 'Produs',
            price: num(op.price, op.unitPrice, product.price),
            quantity: Number(op.quantity) || 1,
            vatBracket: Number(product.vatBracket) || 19,
            kind: 'product',
            thumbnail: product.thumbnail ?? null,
            discounts: [],
        });
    }

    for (const oc of data?.cards ?? []) {
        const card = oc.card ?? {};
        lines.push({
            id: `single-${card.id}`,
            name: card.shortName ?? card.name ?? 'Carte',
            price: num(oc.price, oc.unitPrice, card.price),
            quantity: Number(oc.quantity) || 1,
            vatBracket: 21,
            kind: 'single',
            thumbnail: card.image ?? null,
            discounts: [],
        });
    }

    return lines;
}

// Resolve a scanned event-ticket QR (GET /shop-sales/reservation/:code, proxied
// through the device) into the same `PosSearchResult` a Bilete tile produces for
// that event (id `event-<eventId>`, event price, VAT 21). The caller dispatches
// loadReservation with it so the confirmation code rides the bon — the sale
// import then confirms this reservation and only mints new ones for extra units
// on the line. Returns null when the reservation is missing or already
// confirmed/cancelled (the API returns null data for those).
export function reservationToResult(data: any): PosSearchResult | null {
    const event = data?.event;
    if (!event?.id) return null;
    return {
        id: `event-${event.id}`,
        name: event.title ?? 'Bilet eveniment',
        price: Number(event.price) || 0,
        quantity: Number(event.capacity) || 999, // stock — mirrors posSearch's COALESCE(capacity, 999)
        vatBracket: 21,
        kind: 'event',
        thumbnail: event.imageLogo ?? null,
    };
}
